/**
 * Registration.
 *
 * Posts to `POST /api/auth/register/`, matching `registerSchema`
 * (refactor/backend/src/modules/users/serializers.ts:177-199). The 201 body is
 * `{user, refresh, access}` (refactor/backend/src/modules/auth/routes.ts:183-187),
 * so a successful registration lands the user straight into a session — same as
 * login.
 *
 * `role` is deliberately NOT sent. An unauthenticated caller has its role forced
 * to TENANT server-side regardless of what it asks for (auth/routes.ts:154-158),
 * and an authenticated AGENT is 403'd for requesting anything but TENANT
 * (auth/routes.ts:149-151). A role selector belongs on the superagent's
 * user-management page, not on public signup.
 *
 * A realtor/agent self-signup path (with a pending-approval gate before first
 * login) has been discussed but is deliberately NOT built here — it needs a
 * new approval state, a login-time gate, and a superagent approval screen, and
 * is being scoped as its own project rather than folded into this page.
 *
 * Styled per `AuthLayout.tsx`'s top comment: the scoped, non-Carbon
 * "auth-hero" look, not the rest of the app's Carbon components.
 */
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Email, Locked, Phone, Security, User } from '@carbon/icons-react';
import { useAuth } from '../../auth/AuthContext';
import { parseApiError } from '../../lib/apiError';
import { AuthField } from '../../components/auth/AuthField';
import { ROUTES, homePathForRole } from '../../routes/paths';

const INITIAL_FORM = {
  first_name: '',
  last_name: '',
  username: '',
  email: '',
  phone: '',
  password: '',
  password2: '',
};

type FormState = typeof INITIAL_FORM;

export function RegisterPage(): React.JSX.Element {
  const { register } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [submitting, setSubmitting] = useState(false);

  function update(field: keyof FormState, value: string): void {
    setForm((previous) => ({ ...previous, [field]: value }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setFieldErrors({});

    // Checked client-side as well as server-side: the backend enforces it
    // (serializers.ts:190-199) but a round trip to be told the two password
    // boxes differ is a poor form experience.
    if (form.password !== form.password2) {
      setError("Password fields didn't match.");
      return;
    }

    setSubmitting(true);
    try {
      const user = await register({
        email: form.email,
        username: form.username,
        password: form.password,
        password2: form.password2,
        first_name: form.first_name,
        last_name: form.last_name,
        // The column is NOT NULL and an empty string is normalised server-side,
        // so an omitted phone is sent as '' rather than null.
        phone: form.phone,
      });
      navigate(homePathForRole(user.role), { replace: true });
    } catch (caught) {
      const parsed = parseApiError(caught);
      setError(parsed.message);
      setFieldErrors(parsed.fieldErrors);
    } finally {
      setSubmitting(false);
    }
  }

  function fieldError(field: string): string | undefined {
    return fieldErrors[field]?.[0];
  }

  return (
    <>
      <span className="auth-hero__badge">Get started</span>
      <h1 className="auth-hero__heading">Create an account</h1>
      <p className="auth-hero__subtitle">Set up tenant access in a couple of minutes.</p>

      <form className="auth-hero__form" onSubmit={(event) => void handleSubmit(event)} noValidate>
        {error !== null && (
          <p role="alert" className="auth-hero__error">
            {error}
          </p>
        )}

        <div className="auth-hero__field-row">
          <AuthField
            id="register-first-name"
            label="First name"
            icon={User}
            name="first_name"
            required
            value={form.first_name}
            onChange={(value) => update('first_name', value)}
          />
          <AuthField
            id="register-last-name"
            label="Last name"
            name="last_name"
            required
            value={form.last_name}
            onChange={(value) => update('last_name', value)}
          />
        </div>

        <AuthField
          id="register-username"
          label="Username"
          icon={User}
          name="username"
          autoComplete="username"
          required
          value={form.username}
          onChange={(value) => update('username', value)}
          error={fieldError('username')}
        />

        <AuthField
          id="register-email"
          label="Email"
          icon={Email}
          type="email"
          name="email"
          autoComplete="email"
          placeholder="you@example.com"
          required
          value={form.email}
          onChange={(value) => update('email', value)}
          error={fieldError('email')}
        />

        <AuthField
          id="register-phone"
          label="Phone (optional)"
          icon={Phone}
          type="tel"
          name="phone"
          autoComplete="tel"
          value={form.phone}
          onChange={(value) => update('phone', value)}
        />

        {/* Backend rule: at least 8 characters and not entirely numeric
            (refactor/backend/src/lib/passwordValidation.ts — Django's
            MinimumLengthValidator + NumericPasswordValidator). */}
        <AuthField
          id="register-password"
          label="Password"
          icon={Locked}
          type="password"
          name="password"
          autoComplete="new-password"
          required
          value={form.password}
          onChange={(value) => update('password', value)}
          error={fieldError('password')}
        />

        <AuthField
          id="register-password2"
          label="Confirm password"
          icon={Locked}
          type="password"
          name="password2"
          autoComplete="new-password"
          required
          value={form.password2}
          onChange={(value) => update('password2', value)}
        />

        <button type="submit" className="auth-hero__submit" disabled={submitting}>
          {submitting ? 'Creating account…' : 'Create account'}
        </button>
      </form>

      <p className="auth-hero__trust">
        <Security size={16} aria-hidden="true" />
        Your details are protected with secure authentication.
      </p>

      <p className="auth-hero__footer">
        Already registered? <Link to={ROUTES.login}>Sign in</Link>
      </p>
    </>
  );
}

export default RegisterPage;
